import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const OverduePaymentAlerts = ({ userRole = 'dentist_client', currentUserId = null }) => {
  const [overduePhases, setOverduePhases] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOverduePayments = async () => {
    try {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const adminRole = userRole === 'administrator';

      // Step 1: fetch overdue payment_phases (no role filter yet)
      const { data: allPhases, error: phasesError } = await supabase
        .from('payment_phases')
        .select(`
          id,
          phase_name,
          amount,
          due_date,
          status,
          invoice_id
        `)
        .lt('due_date', today)
        .not('status', 'in', '("paid","cancelled")')
        .order('due_date', { ascending: true });

      if (phasesError) throw phasesError;
      if (!allPhases || allPhases.length === 0) {
        setOverduePhases([]);
        return;
      }

      // Step 2: collect all invoice IDs from phases
      const allInvoiceIds = [...new Set(allPhases.map((p) => p.invoice_id).filter(Boolean))];
      if (allInvoiceIds.length === 0) {
        setOverduePhases([]);
        return;
      }

      // Step 3: fetch matching invoices
      let invoicesQuery = supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          client_name,
          user_profile_id,
          countries (
            currency_code
          )
        `)
        .in('id', allInvoiceIds);

      // For non-admin: restrict to the current user's invoices only
      if (!adminRole) {
        if (!currentUserId) {
          setOverduePhases([]);
          return;
        }
        invoicesQuery = invoicesQuery.eq('user_profile_id', currentUserId);
      }

      const { data: invoicesData, error: invoicesError } = await invoicesQuery;
      if (invoicesError) throw invoicesError;

      if (!invoicesData || invoicesData.length === 0) {
        setOverduePhases([]);
        return;
      }

      // Step 4: build invoice lookup map
      const invoiceMap = {};
      for (const inv of invoicesData) {
        invoiceMap[inv.id] = inv;
      }

      // Step 5: join phases with their invoices (only keep phases that have a matching invoice)
      const today_date = new Date();
      const data = allPhases.filter((p) => invoiceMap[p.invoice_id]);

      const mapped = data.map((phase) => {
        const inv = invoiceMap[phase.invoice_id];
        const dueDate = new Date(phase.due_date);
        const daysOverdue = Math.floor((today_date - dueDate) / (1000 * 60 * 60 * 24));
        let severity = 'low';
        if (daysOverdue >= 14) severity = 'high';
        else if (daysOverdue >= 7) severity = 'medium';

        return {
          id: phase.id,
          invoiceNumber: inv?.invoice_number || 'N/A',
          clientName: inv?.client_name || 'N/A',
          phaseName: phase.phase_name,
          amount: phase.amount,
          dueDate: phase.due_date,
          daysOverdue,
          severity,
          currencyCode: inv?.countries?.currency_code || 'EUR',
        };
      });

      setOverduePhases(mapped);
    } catch (error) {
      console.error('Erreur lors du chargement des paiements en retard :', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverduePayments();
    const interval = setInterval(fetchOverduePayments, 300000);
    return () => clearInterval(interval);
  }, [currentUserId, userRole]);

  const getSeverityConfig = (severity) => {
    const configs = {
      high: {
        bgColor: 'bg-error/10',
        borderColor: 'border-error/30',
        textColor: 'text-error',
        icon: 'AlertCircle',
        label: 'Critique',
      },
      medium: {
        bgColor: 'bg-warning/10',
        borderColor: 'border-warning/30',
        textColor: 'text-warning',
        icon: 'AlertTriangle',
        label: 'Modéré',
      },
      low: {
        bgColor: 'bg-primary/10',
        borderColor: 'border-primary/30',
        textColor: 'text-primary',
        icon: 'Info',
        label: 'Faible',
      },
    };
    return configs?.[severity] || configs?.low;
  };

  const formatAmount = (value, currencyCode = 'EUR') =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currencyCode,
    }).format(value);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="Clock" size={24} className="text-warning animate-pulse" />
          <h3 className="text-lg font-semibold text-foreground">Paiements en retard</h3>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse bg-muted rounded-lg h-20" />
          ))}
        </div>
      </div>
    );
  }

  if (overduePhases?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="CheckCircle" size={24} className="text-success" />
          <h3 className="text-lg font-semibold text-foreground">Paiements en retard</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8 gap-2">
          <Icon name="CheckCircle" size={40} className="text-success opacity-60" />
          <p className="text-sm text-muted-foreground">Aucun paiement en retard pour le moment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Icon name="Clock" size={24} className="text-warning" />
          <h3 className="text-lg font-semibold text-foreground">Paiements en retard</h3>
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-error text-white text-xs font-bold">
            {overduePhases?.length}
          </span>
        </div>
      </div>

      <div className="space-y-3 max-h-[340px] overflow-y-auto">
        {overduePhases?.map((phase) => {
          const config = getSeverityConfig(phase?.severity);
          return (
            <div
              key={phase?.id}
              className={`${config?.bgColor} border ${config?.borderColor} rounded-lg p-4`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-3 flex-1">
                  <Icon name={config?.icon} size={20} className={config?.textColor} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="text-sm font-semibold text-foreground">{phase?.invoiceNumber}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${config?.bgColor} ${config?.textColor} font-medium`}>
                        {config?.label}
                      </span>
                    </div>
                    <p className="text-sm text-foreground mb-1">{phase?.clientName}</p>
                    <p className="text-xs text-muted-foreground">{phase?.phaseName}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-3">
                  <p className="text-lg font-bold text-foreground">
                    {formatAmount(phase?.amount, phase?.currencyCode)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Échéance : {formatDate(phase?.dueDate)}
                  </p>
                </div>
              </div>
              <div className={`flex items-center gap-2 mt-3 pt-3 border-t ${config?.borderColor}`}>
                <Icon name="AlertCircle" size={14} className={config?.textColor} />
                <span className={`text-xs font-medium ${config?.textColor}`}>
                  En retard de {phase?.daysOverdue} {phase?.daysOverdue > 1 ? 'jours' : 'jour'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OverduePaymentAlerts;